import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { LoggerService } from "../../services/logger.service";
import { VFIconComponent } from "../vficon/vficon.component";
import { CommonModule } from "@angular/common";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { FilterItem, TableColumn } from "../../models/common-table";
import { DownloadType, REGEX_COLUMN_SEARCH } from "../../constant/common-table";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
@Component({
    imports: [
        CommonModule,
        VFIconComponent,
        ReactiveFormsModule,
        TooltipModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
    ],
    selector: "app-table-search",
    templateUrl: "./table-search.component.html",
    styleUrls: ["./table-search.component.scss"]
})
export class TableSearchComponent implements AfterViewChecked, OnChanges, OnInit {
  private log = inject(LoggerService);

  public filtered = false;
  @Input() tableName: string = "";
  @Input() disabled: boolean = false;
  @Input() columns: TableColumn[];
  @Input() filterItems: FilterItem[] = [];
  @Input() downloadSelected: boolean = false;
  @Input() disableDownload: boolean = false;
  @Input() lowerCaseInput = true;
  @Output() changeEvent: EventEmitter<FilterItem[]> = new EventEmitter<FilterItem[]>();
  @Output() downloadEvent = new EventEmitter<DownloadType>();
  @Input() isDynamic = false;
  @Input() enableExactMatch: boolean = false;

  // this must be here, please don't remove
  public form = new FormGroup({});

  public isSearchBoxOpen: boolean = false;
  public searchText = new FormControl("", [Validators.required]);
  public isInputLoaded: boolean = false;
  public searchColumn: string = "";
  public typedColumn: string = "";
  public columnsDropdown: boolean = false;
  public sortedColumns: TableColumn[] = [];
  public exactMatch: boolean = false;

  public searchedFilterItems: (FilterItem & { idx?: number })[] = [];

  // HTML element references
  @ViewChild("searchTextInput") searchTextInput: ElementRef;
  @ViewChild("searchTextContainer") searchTextContainer: ElementRef;

  // listeners
  @HostListener("click", ["$event.target"])
  public clickOutside(element: HTMLElement) {
    if (!this.searchTextContainer?.nativeElement?.contains(element)) {
      this.columnsDropdown = false;
    }
  }

  ngOnInit() {
    this.changeEvent.subscribe((filterItems) => {
      this.log.info("changed filter items to ", filterItems);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.columns?.firstChange) {
      this.log.info("Search Table Changes: ", { changes });
      this.sortedColumns = this.getSortedColumns([...this.columns]);
    }
    if (changes?.filterItems) {
      this.filtered = this.filterItems.length > 0;
      this.splitFilterItems();
    }
  }

  ngAfterViewChecked() {
    if (this.isInputLoaded) {
      this.searchTextInput?.nativeElement?.focus();
      this.isInputLoaded = false;
    }
    if (document.activeElement === this.searchTextInput?.nativeElement && this.typedColumn === "") {
      this.openColumnsDropdown(true);
    }
    if (this.searchText.value === "") {
      this.searchColumn = "";
      this.typedColumn = "";
      this.exactMatch = false;
      this.sortedColumns = this.getSortedColumns([...this.columns]);
      this.openColumnsDropdown(true);
    }
  }

  public getSortedColumns(arr: TableColumn[]): TableColumn[] {
    return arr.sort((a, b) => (a.label || a.title).localeCompare(b.label || b.title));
  }

  public openSearchBox(open: boolean): void {
    this.isSearchBoxOpen = open;
    this.isInputLoaded = true;
    if (!open) this.resetFilterForm();
  }

  //reset search text and column
  public resetFilterForm(): void {
    this.searchText.setValue("");
    this.searchColumn = "";
    this.typedColumn = "";
    this.exactMatch = false;
  }

  public openColumnsDropdown(open: boolean) {
    this.columnsDropdown = open;
  }

  public searchColumns(event: KeyboardEvent): void {
    let text: string = (<HTMLInputElement>event.target).value;
    const match = this.columns.filter((col: TableColumn) => {
      return col.label.toLowerCase().includes(text.toLowerCase());
    });
    if (match) {
      this.sortedColumns = this.getSortedColumns([...match]);
    }
  }

  public selectColumn(column: Partial<TableColumn>): void {
    this.searchColumn = column.title;
    this.typedColumn = column.label;
    this.searchText.setValue(`${column.label || column.title} ${this.exactMatch ? "=" : "::"} `);
    this.searchTextInput.nativeElement.focus();
    this.columnsDropdown = false;
  }

  public onFilterFormSubmit(): void {
    this.filtered = true;
    let value = this.searchText.value.trim();
    const match = REGEX_COLUMN_SEARCH.exec(value);
    let columnLabel = "";
    let searchText = "";
    let exactMatch = false;

    if (!value) {
      this.searchTextInput.nativeElement.style.color = "red";
      return;
    } else if (match !== null) {
      columnLabel = match[1];
      exactMatch = match[2] === "=";
      searchText = match[3];
      searchText = this.unwrapQuotes(searchText);
      if (!columnLabel || !searchText) {
        this.searchTextInput.nativeElement.style.color = "red";
        return;
      }
    } else {
      columnLabel = "ALL";
      searchText = value;
      searchText = this.unwrapQuotes(searchText);
      this.searchColumn = "all";
    }

    // check if this column already queried
    const index: number = this.filterItems.findIndex(
      (filterItem) => (filterItem.columnLabel || filterItem.columnTitle).toLowerCase() === columnLabel.toLowerCase(),
    );

    const newFilterItems = JSON.parse(JSON.stringify(this.filterItems));
    searchText = exactMatch ? `=${searchText}` : searchText;
    if (index !== -1) {
      newFilterItems[index].searchText.push(this.lowerCaseInput ? searchText.toLowerCase() : searchText);
    } else {
      if (!this.searchColumn) {
        for (const column of this.sortedColumns) {
          if ((column.label || column.title).toLowerCase() === columnLabel.toLowerCase()) {
            this.searchColumn = column.title;
            columnLabel = column.label || column.title;
            break;
          }
        }
      }
      newFilterItems.push({
        searchText: [this.lowerCaseInput ? searchText.toLowerCase() : searchText],
        columnTitle: this.searchColumn,
        exactMatch,
        columnLabel,
      });
    }
    this.openSearchBox(false);
    this.sortedColumns = this.getSortedColumns([...this.columns]);
    this.changeEvent.emit(newFilterItems);
  }

  public removeFilterItem(index: number, exactMatch: boolean): void {
    this.log.info(
      "removing filter item",
      index,
      this.filterItems[index],
      exactMatch ? "Exact match search text values" : "Contain search text values",
    );

    let filterItems = JSON.parse(JSON.stringify(this.filterItems));
    let searchText = filterItems[index].searchText;
    if (exactMatch) {
      filterItems[index].searchText = searchText.filter((text: string) => !text.startsWith("="));
    } else {
      filterItems[index].searchText = searchText.filter((text: string) => text.startsWith("="));
    }
    if (filterItems[index].searchText?.length === 0) filterItems = this.filterItems.filter((_, idx) => idx !== index);

    this.changeEvent.emit(filterItems);
  }

  public clickDownload(downloadType: DownloadType): void {
    this.downloadEvent.emit(downloadType);
  }

  public toggleExactMatch(): void {
    this.exactMatch = !this.exactMatch;
    if (this.searchText.value) {
      let value = this.searchText.value;
      let separators = this.exactMatch ? ["::", "="] : ["=", "::"];
      this.searchText.setValue(value.replace(separators[0], separators[1]));
    }
  }

  public splitFilterItems(): void {
    let newSearchedFilterItems: (FilterItem & { idx?: number })[] = [];

    this.filterItems.forEach((filterItem, idx) => {
      let exactMatch = filterItem.searchText.filter((text) => text.startsWith("=")).map((text) => text.slice(1));
      let containMatch = filterItem.searchText.filter((text) => !text.startsWith("="));
      if (exactMatch?.length > 0) {
        newSearchedFilterItems.push({
          ...filterItem,
          searchText: exactMatch,
          exactMatch: true,
          idx,
        });
      }
      if (containMatch?.length > 0) {
        newSearchedFilterItems.push({
          ...filterItem,
          searchText: containMatch,
          exactMatch: false,
          idx,
        });
      }
    });

    this.searchedFilterItems = newSearchedFilterItems;
  }
  private unwrapQuotes(input: string): string {
    if (!input) return input;
    const trimmed = input.trim();
    const hasDouble = trimmed.startsWith('"') && trimmed.endsWith('"');
    const hasSingle = trimmed.startsWith("'") && trimmed.endsWith("'");
    if ((hasDouble || hasSingle) && trimmed.length >= 2) {
      return trimmed.slice(1, -1);
    }
    return trimmed;
  }
}
